import { useState, useRef } from "react";
import { db } from "../../firebase";
import { collection, addDoc, serverTimestamp } from "firebase/firestore";
import { SelectField, PlaceAutocomplete, useToast } from "../shared";
import { RouteSelector } from "./TripDetails";

// ── Helpers ───────────────────────────────────────────────────────────────────
// Keep at most `max` evenly-spaced points so Firestore docs stay small
export const downsampleCoords = (coords, max = 300) => {
  if (!coords?.length || coords.length <= max) return coords;
  const step = coords.length / max;
  return Array.from({ length: max }, (_, i) => coords[Math.round(i * step)]);
};

// ── Post Screen ───────────────────────────────────────────────────────────────
const FUEL_L_PER_100KM = 8;
const FUEL_PRICE_EUR   = 1.90;

const PostTrip = ({ currentUser }) => {
  const toast = useToast();
  const [form, setForm]                   = useState({ from: "", to: "", datetime: "", seats: "3" });
  const [fromCoords, setFromCoords]       = useState(null);
  const [toCoords,   setToCoords]         = useState(null);
  const [selectedRoute, setSelectedRoute] = useState(null); // { coords, distanceKm, durationMin }
  const [success, setSuccess]             = useState(false);
  const [err, setErr]                     = useState("");
  const [busy, setBusy]                   = useState(false);
  const [waypoints, setWaypoints]         = useState([]);
  const [addingStop, setAddingStop]       = useState(false);
  const [stopLabel,  setStopLabel]        = useState("");
  const stopLabelRef = useRef("");

  const totalPrice   = selectedRoute ? Math.ceil((selectedRoute.distanceKm * FUEL_L_PER_100KM / 100) * FUEL_PRICE_EUR) : null;
  const pricePerSeat = totalPrice ? Math.ceil((totalPrice / parseInt(form.seats)) * 100) / 100 : null;

  const post = async () => {
    setErr("");
    if (!form.from || !form.to || !form.datetime) { setErr("Please fill all fields."); return; }
    if (new Date(form.datetime) <= new Date()) { setErr("Trip date and time must be in the future."); return; }
    if (!selectedRoute) { setErr("Select a route on the map before posting."); return; }
    setBusy(true);
    try {
      await addDoc(collection(db, "trips"), {
        driverId: currentUser.id, driverName: currentUser.name,
        driverAvatar: currentUser.avatar, driverRating: currentUser.rating || 0,
        driverRatingCount: currentUser.ratingCount || 0,
        from: form.from, to: form.to, datetime: form.datetime,
        fromLat: fromCoords?.lat || null, fromLon: fromCoords?.lon || null,
        toLat: toCoords?.lat || null, toLon: toCoords?.lon || null,
        distanceKm: selectedRoute.distanceKm,
        routeGeometry: downsampleCoords(selectedRoute.coords, 300),
        seats: parseInt(form.seats), totalPrice, price: pricePerSeat,
        waypoints: waypoints.filter(w => w.lat && w.lon).map(({ label, lat, lon }) => ({ label, lat, lon })),
        notes: form.notes || "",
        status: "active", passengers: [], createdAt: serverTimestamp(),
      });
      setSuccess(true);
      toast("Trip posted! Passengers can now find your ride.", "success");
      setForm({ from: "", to: "", datetime: "", seats: "3", notes: "" });
      setFromCoords(null); setToCoords(null); setSelectedRoute(null);
      setWaypoints([]); setAddingStop(false);
      setTimeout(() => setSuccess(false), 3500);
    } catch (e) { console.error(e); setErr(`Failed to post trip: ${e.message || "Please try again."}`); }
    setBusy(false);
  };

  return (
    <div className="screen" style={{ padding: "20px 16px calc(100px + env(safe-area-inset-bottom, 20px))" }}>
    <div className="desk-container">
      <h2 style={{ fontFamily: "Syne", fontWeight: 800, fontSize: 24, color: "var(--ink)", marginBottom: 4 }}>Post a Trip</h2>
      <p style={{ color: "var(--muted)", fontSize: 13, marginBottom: 20 }}>Share your route and earn on the way.</p>
      <div style={{ display: "flex", flexDirection: "column", gap: 13, background: "var(--card)", borderRadius: 20, padding: 16, border: "1.5px solid var(--border)", boxShadow: "var(--shadow)", overflow: "hidden", boxSizing: "border-box", width: "100%" }}>
        <PlaceAutocomplete label="From" placeholder="e.g. Kyperounda" value={form.from} onChange={v => { setForm(f => ({ ...f, from: v })); setFromCoords(null); setSelectedRoute(null); }} onCoords={setFromCoords}/>
        <PlaceAutocomplete label="To"   placeholder="e.g. Trimiklini"  value={form.to}   onChange={v => { setForm(f => ({ ...f, to: v })); setToCoords(null); setSelectedRoute(null); }} onCoords={setToCoords}/>
        <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
          <label style={{ fontSize: 11, fontWeight: 700, color: "var(--muted)", fontFamily: "Syne", letterSpacing: "0.07em", textTransform: "uppercase" }}>Date &amp; Time</label>
          <div style={{ display: "flex", flexDirection: "row", gap: 8, width: "100%" }}>
            <input type="date" value={form.datetime ? form.datetime.split("T")[0] : ""} onChange={e => { const time = form.datetime ? (form.datetime.split("T")[1] || "00:00") : "00:00"; setForm(f => ({ ...f, datetime: e.target.value ? `${e.target.value}T${time}` : "" })); }} style={{ width: "50%", boxSizing: "border-box", padding: "12px 14px", borderRadius: 12, border: "1.5px solid var(--border)", background: "var(--card)", fontSize: 14, color: "var(--ink)", transition: "border-color .15s" }}/>
            <input type="time" value={form.datetime ? (form.datetime.split("T")[1] || "") : ""} onChange={e => { const date = form.datetime ? form.datetime.split("T")[0] : ""; setForm(f => ({ ...f, datetime: date && e.target.value ? `${date}T${e.target.value}` : "" })); }} style={{ width: "50%", boxSizing: "border-box", padding: "12px 14px", borderRadius: 12, border: "1.5px solid var(--border)", background: "var(--card)", fontSize: 14, color: "var(--ink)", transition: "border-color .15s" }}/>
          </div>
        </div>
        <SelectField label="Seats available" value={form.seats} onChange={e => setForm(f => ({ ...f, seats: e.target.value }))}>
          {[1,2,3,4,5,6].map(n => <option key={n} value={n}>{n} seat{n !== 1 ? "s" : ""}</option>)}
        </SelectField>
        <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
          <label style={{ fontSize: 11, fontWeight: 700, color: "var(--muted)", fontFamily: "Syne", letterSpacing: "0.07em", textTransform: "uppercase" }}>Notes (optional)</label>
          <input placeholder="AC, luggage space, pet-friendly..." value={form.notes || ""} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} style={{ padding: "12px 14px", borderRadius: 12, border: "1.5px solid var(--border)", background: "var(--card)", fontSize: 14, color: "var(--ink)", transition: "border-color .15s" }}/>
        </div>
        {fromCoords && toCoords && (
          <div>
            <div style={{ fontSize: 11, fontFamily: "Syne", fontWeight: 700, color: "var(--muted)", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 8 }}>Via stops (optional)</div>
            {waypoints.map((wp, i) => (
              <div key={wp.id} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                <span style={{ width: 8, height: 8, borderRadius: "50%", background: "#9b59b6", flexShrink: 0 }}/>
                <span style={{ flex: 1, fontSize: 13, color: "var(--ink)" }}>{wp.label}</span>
                <button onClick={() => setWaypoints(wps => wps.filter((_, j) => j !== i))} style={{ fontSize: 18, color: "var(--muted)", lineHeight: 1, padding: "2px 6px", background: "none", border: "none" }}>×</button>
              </div>
            ))}
            {addingStop ? (
              <PlaceAutocomplete
                placeholder="Search for a stop..."
                value={stopLabel}
                onChange={v => { setStopLabel(v); stopLabelRef.current = v; }}
                onCoords={coords => {
                  setWaypoints(wps => [...wps, { id: Date.now(), label: stopLabelRef.current, lat: coords.lat, lon: coords.lon }]);
                  setStopLabel(""); stopLabelRef.current = ""; setAddingStop(false);
                }}
              />
            ) : (
              <button onClick={() => setAddingStop(true)} style={{ fontSize: 12, color: "var(--accent)", fontFamily: "Syne", fontWeight: 700, padding: "4px 0", background: "none", border: "none" }}>+ Add stop</button>
            )}
          </div>
        )}
        <RouteSelector fromCoords={fromCoords} toCoords={toCoords} waypoints={waypoints} onChange={setSelectedRoute}/>
        <div style={{ background: "var(--warm)", borderRadius: 12, padding: "12px 14px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div>
            <div style={{ fontSize: 11, fontFamily: "Syne", fontWeight: 700, color: "var(--muted)", textTransform: "uppercase", letterSpacing: "0.07em" }}>Fare (auto-calculated)</div>
            <div style={{ fontSize: 12, color: "var(--muted)", marginTop: 2 }}>Based on fuel cost for selected route</div>
          </div>
          <div style={{ textAlign: "right" }}>
            {totalPrice ? (
              <>
                <div style={{ fontFamily: "Syne", fontWeight: 800, fontSize: 18, color: "var(--accent)" }}>€{totalPrice} total</div>
                <div style={{ fontSize: 11, color: "var(--muted)" }}>€{pricePerSeat}/seat · {selectedRoute.distanceKm.toFixed(1)} km</div>
              </>
            ) : (
              <span style={{ fontSize: 12, color: "var(--muted)" }}>Select route first</span>
            )}
          </div>
        </div>
        {err && <div style={{ background: "var(--error-bg)", border: "1px solid var(--error-border)", borderRadius: 10, padding: "10px 14px", color: "#c0392b", fontSize: 13, fontWeight: 500 }}>{err}</div>}
        <button onClick={post} disabled={busy} style={{ width: "100%", padding: 18, background: "linear-gradient(135deg, #2b5be0, #5a80f0)", color: "#fff", borderRadius: 16, fontFamily: "Syne", fontWeight: 700, fontSize: 16, border: "none", cursor: "pointer", boxShadow: "0 4px 16px rgba(43,91,224,.25)", transition: "all .2s", opacity: busy ? 0.5 : 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
          {busy ? <span style={{ width: 16, height: 16, border: "2px solid rgba(255,255,255,.4)", borderTopColor: "#fff", borderRadius: "50%", animation: "igo-spin .7s linear infinite" }}/> : <><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><path d="M12 5v14M5 12h14"/></svg>Post Trip</>}
        </button>
      </div>
    </div></div>
  );
};

export default PostTrip;
